import { useEffect, useState, useMemo, useCallback } from "react";
import { api } from "@/lib/api";
import { formatNaira } from "@/lib/format";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell } from "recharts";
import { Users, Calendar, Footprints, AlertTriangle, TrendingUp, Wallet } from "lucide-react";

const CHART_MARGIN = { top: 8, right: 8, left: 0, bottom: 0 };
const TOOLTIP_STYLE = { background: "#0D1B2A", border: "1px solid #2A3A4A", borderRadius: 8 };
const TOOLTIP_CURSOR = { fill: "rgba(46,202,200,0.06)" };
const AXIS_TICK = { fill: "#8CA0B8" };
const TOOLTIP_LABEL = { color: "#fff" };
const BAR_RADIUS = [4, 4, 0, 0];
const PIE_COLORS = { Paid: "#2D9E5F", Outstanding: "#E63946" };

const Tile = ({ icon: Icon, label, value, sub, accent }) => (
  <div className="kpi" data-testid={`kpi-${label}`}>
    <div className="flex items-center justify-between">
      <div className="kpi-label" style={{ textTransform: "uppercase" }}>{label}</div>
      <Icon size={16} color={accent || "#2ECAC8"} />
    </div>
    <div className="kpi-value">{value}</div>
    {sub && <div className="kpi-sub">{sub}</div>}
  </div>
);

export const Dashboard = () => {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get("/analytics/dashboard");
    setData(r.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const incidents = useMemo(
    () => (data?.incidents_by_type || []).map(i => ({ name: i.category, count: i.count })),
    [data]
  );
  const revenuePie = useMemo(() => data ? [
    { name: "Paid", value: data.revenue_paid },
    { name: "Outstanding", value: data.revenue_outstanding },
  ] : [], [data]);
  const incidentTotal = useMemo(
    () => incidents.reduce((s, i) => s + i.count, 0),
    [incidents]
  );

  if (!data) return <div className="text-muted">Loading dashboard...</div>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Tile icon={Users} label="Active guards" value={data.active_guards} sub="On duty now" />
        <Tile icon={Calendar} label="Shifts (7d)" value={data.shifts_completed_week}
              sub={`${data.shifts_missed_week} missed`} />
        <Tile icon={Footprints} label="Tours (30d)" value={data.tours_completed_month} sub="Completed" />
        <Tile icon={AlertTriangle} label="Incidents (30d)" value={incidentTotal}
              accent="#E63946" sub="Across all sites" />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="s-card">
          <div className="flex items-center justify-between mb-4">
            <div className="h-section">Incidents by type · 30 days</div>
            <AlertTriangle size={16} color="#E63946" />
          </div>
          <div style={{ height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={incidents} margin={CHART_MARGIN}>
                <XAxis dataKey="name" stroke="#8CA0B8" fontSize={11} tick={AXIS_TICK} />
                <YAxis stroke="#8CA0B8" fontSize={11} tick={AXIS_TICK} />
                <Tooltip cursor={TOOLTIP_CURSOR}
                         contentStyle={TOOLTIP_STYLE}
                         labelStyle={TOOLTIP_LABEL} />
                <Bar dataKey="count" fill="#2ECAC8" radius={BAR_RADIUS} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="s-card">
          <div className="flex items-center justify-between mb-4">
            <div className="h-section">Revenue · Naira</div>
            <Wallet size={16} color="#2ECAC8" />
          </div>
          <div style={{ height: 260 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={revenuePie} dataKey="value" nameKey="name" cx="50%" cy="50%"
                     innerRadius={50} outerRadius={90} paddingAngle={3}>
                  {revenuePie.map((entry) => <Cell key={entry.name} fill={PIE_COLORS[entry.name]} />)}
                </Pie>
                <Tooltip
                  formatter={(v) => formatNaira(v)}
                  contentStyle={TOOLTIP_STYLE} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <div className="text-muted" style={{ fontSize: 12 }}>Paid</div>
              <div className="text-success" style={{ fontSize: 18 }}>{formatNaira(data.revenue_paid)}</div>
            </div>
            <div>
              <div className="text-muted" style={{ fontSize: 12 }}>Outstanding</div>
              <div className="text-alert" style={{ fontSize: 18 }}>{formatNaira(data.revenue_outstanding)}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="s-card">
        <div className="flex items-center justify-between mb-4">
          <div className="h-section">Top performing guards</div>
          <TrendingUp size={16} color="#2ECAC8" />
        </div>
        <table className="s-table">
          <thead><tr><th>Guard</th><th>Score</th></tr></thead>
          <tbody>
            {(data.top_guards || []).map(g => (
              <tr key={g.id} data-testid={`top-guard-${g.id}`}>
                <td>{g.name}</td>
                <td>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ flex: 1, background: "#1F3245", borderRadius: 4, height: 6, maxWidth: 200 }}>
                      <div style={{ width: `${g.score}%`, background: "#2ECAC8", height: 6, borderRadius: 4 }} />
                    </div>
                    <span className="mono text-teal">{g.score}</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
